import React, { useEffect, useState } from 'react';
import { Box, Text, Button, FormControl, FormLabel, FormErrorMessage, useToast } from '@chakra-ui/react';

import Breadcrumb from '../components/layout/Breadcrumb';

import { useUserContext } from '../contexts/UserContext';
import { getUserById, updateUser } from '../services/UserServices';
import { detectCountry, parseStoredPhone, isValidPhoneFor, toStoredPhone } from '../validations/PhoneValidations';
import PhoneInput from '../components/PhoneInput';
import AddressForm, { emptyAddress, addressFromUser, composeAddress, validateAddress } from '../components/AddressForm';

// Brings the first highlighted field into view so the customer sees what to fix
const focusFirstError = () => setTimeout(() => {
  const field = document.querySelector('[aria-invalid="true"]');
  if (field) {
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    field.focus({ preventScroll: true });
  }
}, 50);

const Infos = () => {

  const toast = useToast();
  const { currentUser } = useUserContext();
  const [address, setAddress] = useState(emptyAddress);
  const [phoneCountry, setPhoneCountry] = useState(detectCountry());
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getUserById(currentUser)
      .then((result) => {
        setAddress(addressFromUser(result.user));
        if (result.user?.phone) {
          const parsed = parseStoredPhone(result.user.phone);
          setPhoneCountry(parsed.country);
          setPhone(parsed.number);
        }
      });
  }, [currentUser]);

  const onClickSave = () => {
    const newErrors = validateAddress(address);
    if (!isValidPhoneFor(phoneCountry, phone)) {
      newErrors.phone = phoneCountry === 'IN' ? 'Enter a valid 10-digit mobile number.' : 'Enter a valid mobile number.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      focusFirstError();
      return;
    }

    setIsSaving(true);
    updateUser(currentUser, composeAddress(address), toStoredPhone(phoneCountry, phone), address)
      .then((result) => {
        toast({
          title: result.status ? 'Error!' : 'Saved!',
          description: result.status ? 'Something went wrong.' : 'Your delivery details are saved for checkout.',
          status: result.status ? 'error' : 'success',
          duration: 2000,
          isClosable: true
        });
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Box>
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'My details' }]} />
      <Box maxWidth='560px' mx='auto' px={{ base: '12px', md: '24px' }} pt={{ base: '20px', md: '32px' }} pb={{ base: '32px', md: '56px' }} >
        <Text as='h1' fontSize={{ base: '28px', md: '36px' }} fontWeight={700} textTransform='uppercase' sx={{ fontStretch: '75%' }} >My details</Text>
        <Text color='raano.muted' mt='4px' mb='20px' fontSize='14px' >Your delivery details. We use these to speed up checkout.</Text>
        <Box border='1px solid' borderColor='raano.border' borderRadius='8px' p={{ base: '16px', md: '24px' }} >
          <AddressForm
            value={address}
            onChange={setAddress}
            errors={errors}
            onClearError={(field) => setErrors(prev => ({ ...prev, [field]: null }))}
          />
          <FormControl isInvalid={!!errors.phone} mt='12px' mb='20px' >
            <FormLabel fontSize='14px' mb='4px' >Mobile number</FormLabel>
            <PhoneInput
              country={phoneCountry}
              number={phone}
              onChange={(country, number) => { setPhoneCountry(country); setPhone(number); setErrors(prev => ({ ...prev, phone: null })); }}
            />
            <FormErrorMessage>{errors.phone}</FormErrorMessage>
          </FormControl>
          <Button variant='ink' width='100%' height='48px' fontSize='16px' onClick={onClickSave} isLoading={isSaving} >Save details</Button>
        </Box>
      </Box>
    </Box>
  )
}

export default Infos;
